import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const fmtTs = (ts) => {
  if (!ts) return "—";
  const n = Number(ts);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleString();
};

const money = (v, cur) => (v || v === 0 ? `${cur && cur !== "USD" ? cur + " " : "$"}${Number(v).toFixed(2)}` : "—");

// eBay order fulfillment/payment statuses → display color
const STATUS_COLORS = {
  NOT_STARTED: "#0033FF",
  IN_PROGRESS: "#f59e0b",
  FULFILLED: "#34d399",
  PAID: "#34d399",
  PENDING: "rgba(10,10,15,0.58)",
  FAILED: "#ef4444",
  CANCELLED: "#ef4444",
};
const fmtStatus = (s) => (
  <span style={{ ...S.typeBadge, color: STATUS_COLORS[s] || "#0A0A0F", fontSize: 12 }}>
    {(s || "—").replace(/_/g, " ")}
  </span>
);

export default function EbayPage({ store }) {
  const storeKey = store === "designshop" ? "designshop" : "vibratovault";
  const storeLabel = storeKey === "designshop" ? "Design Shop" : "Vibrato Vault";

  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [listings, setListings] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [edits, setEdits] = useState({}); // itemId -> { sku, price, quantity }
  const [savingAll, setSavingAll] = useState(false);

  const [settings, setSettings] = useState({
    connected: false, appConfigured: false, sellerName: "",
    lastSynced: "", tokenExpiresAt: "",
  });

  // CEO-only app credential form
  const [cred, setCred] = useState({ empId: "", password: "", appId: "", certId: "", ruName: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const connectPoll = useRef(null);

  const fetchSettings = useCallback(() => {
    fetch(`${API_URL}/api/ebay/settings?store=${storeKey}`)
      .then((r) => r.json())
      .then((s) => setSettings((prev) => ({ ...prev, ...s })))
      .catch(() => {});
  }, [storeKey]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/ebay/orders?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.orders) setOrders(data.orders);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  const fetchListings = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/ebay/trading-listings?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.listings) setListings(data.listings);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/ebay/payouts?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.transactions) setPayouts(data.transactions);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => {
    if (tab === "orders") fetchOrders();
    if (tab === "listings" && settings.connected) fetchListings();
    if (tab === "payouts" && settings.connected) fetchPayouts();
  }, [tab, settings.connected, fetchOrders, fetchListings, fetchPayouts]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Surface the OAuth callback result if the user landed back here with a param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("ebay_connected")) {
      setToast("Connected to eBay ✓");
      fetchSettings();
    } else if (params.get("ebay_error")) {
      setError(`eBay connection failed: ${params.get("ebay_error")}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback auto-sync while the page is open (server poller also runs every 5 min)
  useEffect(() => {
    if (!settings.connected) return undefined;
    const iv = setInterval(() => {
      fetch(`${API_URL}/api/ebay/sync?store=${storeKey}`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => { if (d && d.synced > 0) { fetchOrders(); fetchSettings(); } })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, [settings.connected, storeKey, fetchOrders, fetchSettings]);

  useEffect(() => () => { if (connectPoll.current) clearInterval(connectPoll.current); }, []);

  const connect = async () => {
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/ebay/oauth/start?store=${storeKey}`);
      const data = await r.json();
      if (data.error) { setError(data.error); return; }
      window.open(data.authorizeUrl, "_blank", "noopener");
      setConnecting(true);
      if (connectPoll.current) clearInterval(connectPoll.current);
      let tries = 0;
      connectPoll.current = setInterval(async () => {
        tries += 1;
        try {
          const s = await fetch(`${API_URL}/api/ebay/settings?store=${storeKey}`).then((x) => x.json());
          if (s.connected) {
            setSettings((prev) => ({ ...prev, ...s }));
            setConnecting(false);
            setToast("Connected to eBay ✓");
            clearInterval(connectPoll.current);
          } else if (tries > 90) {
            setConnecting(false);
            clearInterval(connectPoll.current);
          }
        } catch (e) { /* ignore transient errors */ }
      }, 2000);
    } catch (e) { setError(`Could not start eBay connect: ${e.message}`); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${storeLabel} from eBay? Inventory will stop auto-syncing until reconnected.`)) return;
    await fetch(`${API_URL}/api/ebay/disconnect?store=${storeKey}`, { method: "POST" }).catch(() => {});
    fetchSettings();
    setToast("Disconnected from eBay");
  };

  const syncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/ebay/sync?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setSyncResult(d);
        const errs = (d.errors || []).length;
        setToast(`Synced ${d.synced} • skipped ${d.skipped || 0}${errs ? ` • ${errs} error${errs > 1 ? "s" : ""}` : ""}`);
        fetchOrders();
        fetchSettings();
      }
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const saveAll = async () => {
    const ids = Object.keys(edits);
    if (!ids.length) return;
    setSavingAll(true);
    setError("");
    let ok = 0;
    const errs = [];
    for (const itemId of ids) {
      const e = edits[itemId] || {};
      const body = {};
      if (e.sku !== undefined) body.sku = e.sku;
      if (e.price !== undefined && e.price !== "") body.price = parseFloat(e.price);
      if (e.quantity !== undefined && e.quantity !== "") body.quantity = parseInt(e.quantity, 10);
      try {
        const r = await fetch(`${API_URL}/api/ebay/trading-listings/${encodeURIComponent(itemId)}?store=${storeKey}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const d = await r.json();
        if (d.error) errs.push(`${itemId}: ${d.error}`);
        else ok += 1;
      } catch (err) { errs.push(`${itemId}: ${err.message}`); }
    }
    setSavingAll(false);
    setEdits({});
    fetchListings();
    if (errs.length) setError(`Saved ${ok}, ${errs.length} failed — ${errs.slice(0, 3).join(" · ")}`);
    else setToast(`Saved ${ok} listing${ok !== 1 ? "s" : ""} to eBay ✓`);
  };

  const saveCredentials = async () => {
    setCredSaving(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/ebay/app-credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cred),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast("eBay app credentials saved ✓");
        setShowCredForm(false);
        setCred({ empId: "", password: "", appId: "", certId: "", ruName: "" });
        fetchSettings();
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setCredSaving(false);
  };

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || "").toLowerCase().includes(s)
      || (o.sku || "").toLowerCase().includes(s)
      || (o.buyerName || "").toLowerCase().includes(s)
      || String(o.orderId).includes(s);
  });

  const filteredListings = listings.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.title || "").toLowerCase().includes(s) || (l.sku || "").toLowerCase().includes(s);
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "listings", label: "Listings" },
    { key: "payouts", label: "Payouts" },
    { key: "connect", label: "Connection" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>eBay Integration</h2>
          <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>direct eBay Sell API · {storeLabel}</div>
        </div>
        {settings.connected && tab !== "connect" && (
          <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1, cursor: syncing ? "wait" : "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            {syncing ? "Syncing…" : "Sync Sales"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid #34d399", borderRadius: 0, fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {syncResult && tab === "orders" && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e", borderRadius: 0, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#34d399", marginBottom: 4 }}>Sync Complete</div>
          <div style={{ fontSize: 14, color: "#0A0A0F" }}>
            {syncResult.synced || 0} synced, {syncResult.skipped || 0} already synced, {(syncResult.errors || []).length} errors
          </div>
          {(syncResult.errors || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              {syncResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 13, color: "#ef4444" }}>
                  Order {e.orderId}: {e.reason} {e.sku ? `(SKU: ${e.sku})` : ""} — {e.title || ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Connection Tab ── */}
      {tab === "connect" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Shop Connection</div>
            {settings.connected ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 0, background: "#34d399", display: "inline-block" }} />
                  <span style={{ fontSize: 16, color: "#0A0A0F", fontWeight: 600 }}>
                    Connected{settings.sellerName ? ` — ${settings.sellerName}` : ""}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>
                  <span>Last synced</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.lastSynced)}</span>
                  <span>Token valid until</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.tokenExpiresAt)}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>{syncing ? "Syncing…" : "Sync Now"}</button>
                  <button onClick={disconnect} style={{ ...S.clearBtn, fontSize: 13 }}>Disconnect</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
                  {settings.appConfigured
                    ? `Authorize ${storeLabel} on eBay. Orders will then sync directly and deduct inventory. Only orders placed after you connect are deducted.`
                    : "Add the eBay app credentials below first (CEO only), then authorize the shop."}
                </p>
                <button onClick={connect} disabled={!settings.appConfigured || connecting} style={{ ...S.addBtn, opacity: (!settings.appConfigured || connecting) ? 0.5 : 1, cursor: (!settings.appConfigured || connecting) ? "not-allowed" : "pointer" }}>
                  {connecting ? "Waiting for eBay…" : `Connect ${storeLabel} to eBay`}
                </button>
                {connecting && (
                  <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 8 }}>Approve access in the eBay tab that opened, then come back here.</div>
                )}
              </div>
            )}
          </div>

          {/* App credentials (CEO only) */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)" }}>eBay App Credentials</div>
              {settings.appConfigured && !showCredForm && (
                <span style={{ fontSize: 13, color: "#34d399", display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  Configured
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
              Your <strong>Production</strong> App ID (Client ID), Cert ID (Client Secret), and RuName from the eBay developer portal. Stored server-side, never shown again. CEO credentials required to save.
            </p>
            {(!settings.appConfigured || showCredForm) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
                <input value={cred.appId} onChange={(e) => setCred({ ...cred, appId: e.target.value })} placeholder="App ID (Client ID)" style={S.input} />
                <input value={cred.certId} onChange={(e) => setCred({ ...cred, certId: e.target.value })} placeholder="Cert ID (Client Secret)" type="password" style={S.input} />
                <input value={cred.ruName} onChange={(e) => setCred({ ...cred, ruName: e.target.value })} placeholder="RuName (redirect URL name)" style={S.input} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input value={cred.empId} onChange={(e) => setCred({ ...cred, empId: e.target.value })} placeholder="CEO Employee ID" style={{ ...S.input, flex: 1 }} />
                  <input value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="CEO Password" type="password" style={{ ...S.input, flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveCredentials} disabled={credSaving} style={{ ...S.addBtn, opacity: credSaving ? 0.6 : 1 }}>{credSaving ? "Saving…" : "Save Credentials"}</button>
                  {settings.appConfigured && <button onClick={() => setShowCredForm(false)} style={{ ...S.clearBtn, fontSize: 13 }}>Cancel</button>}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCredForm(true)} style={{ ...S.clearBtn, fontSize: 13 }}>Update Credentials</button>
            )}
          </div>

          {/* Redirect URL reminder */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>eBay App Setup</div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.6 }}>
              In your eBay developer account → User Tokens → your RuName, set this exact <strong>Auth Accepted URL</strong>:
            </p>
            <div style={{ marginTop: 8, padding: "10px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#0033FF", wordBreak: "break-all" }}>
              {API_URL.replace(/\/$/, "")}/api/ebay/oauth/callback
            </div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.6, marginTop: 16 }}>
              eBay also requires a <strong>Marketplace Account Deletion</strong> notification endpoint on your production keyset (Developer portal → Alerts &amp; Notifications). Use this URL + verification token:
            </p>
            <div style={{ marginTop: 8, padding: "10px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#0033FF", wordBreak: "break-all" }}>
              {settings.notificationEndpoint || `${API_URL.replace(/\/$/, "")}/api/ebay/notifications`}
            </div>
            {settings.verificationToken ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Verification token</div>
                <div style={{ padding: "8px 12px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#0A0A0F", wordBreak: "break-all" }}>
                  {settings.verificationToken}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 6 }}>Save your app credentials first to generate the verification token.</div>
            )}
          </div>
        </div>
      )}

      {/* ── Orders Tab (synced sales) ── */}
      {tab === "orders" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ ...S.input, flex: 1, maxWidth: 300 }} />
            <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{filteredOrders.length} orders</div>
            <button onClick={fetchOrders} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {!settings.connected && orders.length === 0 && (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to eBay yet</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to eBay</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading orders...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Order #</th>
                    <th style={S.th}>Buyer</th>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={S.th}>Status</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                    <th style={S.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const items = o.items || [];
                    const hasMultiple = items.length > 1;
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <tr onClick={() => hasMultiple && setExpandedOrder(isExpanded ? null : o.id)} style={{ cursor: hasMultiple ? "pointer" : "default" }}>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                            {hasMultiple && <span style={{ display: "inline-block", width: 16, color: "rgba(10,10,15,0.58)", fontSize: 12 }}>{isExpanded ? "▾" : "▸"}</span>}
                            {o.orderId}
                          </td>
                          <td style={{ ...S.td, fontSize: 14, color: "#0A0A0F" }}>{o.buyerName || "—"}</td>
                          <td style={{ ...S.td, maxWidth: 220 }}>
                            <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.title || "—"}</div>
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: o.sku ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{o.sku || "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>{money(o.amount, o.currency)}</td>
                          <td style={S.td}>{fmtStatus(o.status)}</td>
                          <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: o.quantityDeducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                            {o.quantityDeducted > 0 ? `-${o.quantityDeducted}` : "0"}
                          </td>
                          <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{o.syncedAt ? new Date(o.syncedAt).toLocaleString() : "—"}</td>
                        </tr>
                        {isExpanded && items.map((item, i) => (
                          <tr key={`${o.id}-${i}`} style={{ background: "#FFFFFF" }}>
                            <td style={{ ...S.td, paddingLeft: 32 }} />
                            <td style={S.td} />
                            <td style={{ ...S.td, maxWidth: 220 }}>
                              <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "rgba(10,10,15,0.58)" }}>{item.sku || "—"}</td>
                            <td style={S.td} />
                            <td style={S.td} />
                            <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: item.deducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                              {item.quantity || 1} qty{item.deducted > 0 ? ` (−${item.deducted})` : ""}
                            </td>
                            <td style={S.td} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && !loading && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>
                      {orders.length === 0 ? "No orders yet. Connect to eBay and orders will sync here automatically." : "No matching orders"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Listings Tab (inventory) ── */}
      {tab === "listings" && (
        <>
          {!settings.connected ? (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to eBay</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to eBay</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search listings..." style={{ ...S.input, flex: 1, maxWidth: 300 }} />
                <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{filteredListings.length} listings</div>
                <div style={{ flex: 1 }} />
                {Object.keys(edits).length > 0 && (
                  <button onClick={saveAll} disabled={savingAll} style={{ ...S.addBtn, opacity: savingAll ? 0.6 : 1, cursor: savingAll ? "wait" : "pointer" }}>
                    {savingAll ? "Saving…" : `Save ${Object.keys(edits).length} change${Object.keys(edits).length !== 1 ? "s" : ""} to eBay`}
                  </button>
                )}
                <button onClick={fetchListings} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                </button>
              </div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading listings from eBay...</div>
              ) : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>SKU (Custom Label)</th>
                        <th style={S.th}>Title</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Price</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredListings.map((l) => {
                        const e = edits[l.itemId] || {};
                        const dirty = e.sku !== undefined || e.price !== undefined || e.quantity !== undefined;
                        return (
                          <tr key={l.itemId} style={dirty ? { background: "rgba(0,51,255,0.06)" } : undefined}>
                            <td style={S.td}>
                              <input
                                value={e.sku !== undefined ? e.sku : (l.sku ?? "")}
                                onChange={(ev) => setEdits((prev) => ({ ...prev, [l.itemId]: { ...prev[l.itemId], sku: ev.target.value } }))}
                                placeholder="set SKU…"
                                style={{ ...S.input, width: 140, fontFamily: "'JetBrains Mono', monospace", padding: "4px 6px" }}
                              />
                            </td>
                            <td style={{ ...S.td, maxWidth: 300 }}>
                              <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <input
                                value={e.price !== undefined ? e.price : (l.price ?? "")}
                                onChange={(ev) => setEdits((prev) => ({ ...prev, [l.itemId]: { ...prev[l.itemId], price: ev.target.value } }))}
                                style={{ ...S.input, width: 80, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", padding: "4px 6px" }}
                              />
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              <input
                                value={e.quantity !== undefined ? e.quantity : (l.quantity ?? "")}
                                onChange={(ev) => setEdits((prev) => ({ ...prev, [l.itemId]: { ...prev[l.itemId], quantity: ev.target.value } }))}
                                style={{ ...S.input, width: 70, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", padding: "4px 6px" }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {filteredListings.length === 0 && !loading && (
                        <tr><td colSpan={4} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No active listings found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Payouts Tab (Finances) ── */}
      {tab === "payouts" && (
        <>
          {!settings.connected ? (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to eBay</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to eBay</button>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
                <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{payouts.length} transactions</div>
                <button onClick={fetchPayouts} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                </button>
              </div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading transactions from eBay...</div>
              ) : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Date</th>
                        <th style={S.th}>Order</th>
                        <th style={S.th}>Type</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Gross</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Fees</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Net</th>
                        <th style={S.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id}>
                          <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.date ? new Date(p.date).toLocaleDateString() : "—"}</td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{p.orderId || "—"}</td>
                          <td style={{ ...S.td, fontSize: 13 }}>{(p.type || "—").replace(/_/g, " ")}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{money(p.gross, p.currency)}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#ef4444" }}>{p.fee ? `-${money(Math.abs(p.fee), p.currency)}` : "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#34d399", fontWeight: 600 }}>{money(p.net, p.currency)}</td>
                          <td style={S.td}>{fmtStatus(p.status)}</td>
                        </tr>
                      ))}
                      {payouts.length === 0 && !loading && (
                        <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>No transactions yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
